import { query } from '@organization/api'
import { useUsersServiceUsersEditPartialUpdate } from '@organization/api/queries'
import { PhoneInput } from '@shared/components'
import { useInvalidateQueries, useSessionContext } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import {
  App,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Typography,
  Upload
} from 'antd'
import { UploadProps } from 'antd/es/upload'
import { RcFile } from 'antd/lib/upload'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const CMS_TOKEN = import.meta.env.VITE_APP_CMS_TOKEN
const CMS_API_URL = import.meta.env.VITE_APP_CMS_API

type ProfileFormProps = {
  form: FormInstance
  onPending: React.Dispatch<React.SetStateAction<boolean>>
}

export const ProfileForm = ({ form, onPending }: ProfileFormProps) => {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)

  const { user, profile } = useSessionContext()
  const { notification } = App.useApp()
  const { invalidateQueries } = useInvalidateQueries()

  const beforeUpload = (file: RcFile): boolean => {
    const isLt10M = file.size / 1024 / 1024 < 20
    if (!isLt10M) {
      showApiErrors(t('error.image_smaller'))
    }
    return isLt10M
  }

  const photo = Form.useWatch('photo', form)

  const uploadProps: UploadProps = {
    showUploadList: false,
    maxCount: 1,
    accept: '.jpg,.png',
    beforeUpload,
    customRequest: async ({ file, onSuccess, onError }) => {
      setUploading(true)
      try {
        if (!CMS_TOKEN) {
          throw new Error('CMS token not found. Please check your URL.')
        }

        const mediaResponse = await axios.post(
          `${CMS_API_URL}/media/create-unauthenticated/`,
          {
            name: (file as File).name
          },
          {
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
              'saas-app-token': CMS_TOKEN
            }
          }
        )

        if (mediaResponse.data?.upload_url) {
          await axios.put(mediaResponse.data.upload_url, file, {
            headers: {
              'Content-Type': 'application/octet-stream'
            }
          })

          const baseUrl = mediaResponse.data.upload_url.split('?')[0]

          form.setFieldValue('photo', baseUrl)
          onSuccess?.(mediaResponse.data)
          notification.success({ message: t('profile.image_uploaded') })
        } else {
          onError?.(new Error('Failed to get upload URL'))
        }
      } catch (error) {
        console.error('Upload error:', error)
        onError?.(error as Error)
        notification.error({ message: t('profile.upload_failed') })
      } finally {
        setUploading(false)
      }
    }
  }

  const { mutate, isPending } = useUsersServiceUsersEditPartialUpdate({
    onSuccess: () => {
      notification.success({ message: t('profile.updated') })
      invalidateQueries([query.users.get().queryKey])
    },
    onError: (error) => {
      showApiErrors(error, form)
    }
  })

  useEffect(() => {
    onPending(isPending)
  }, [isPending, onPending])

  const onSubmit = () => {
    const values = form.getFieldsValue()

    mutate({
      requestBody: {
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone,
        position: values.position,
        photo: photo ?? user?.user?.photo
      }
    })
  }

  useEffect(() => {
    if (user) {
      form.setFields([
        { name: 'first_name', value: user.user.first_name },
        { name: 'last_name', value: user.user.last_name },
        { name: 'phone', value: user.user.phone },
        { name: 'position', value: user.user.position }
      ])
    }
  }, [user, form])

  return (
    <>
      <Card.Meta
        avatar={<Avatar size={72} src={photo ?? user?.user?.photo} />}
        title={t('profile.photo')}
        description={
          <Flex vertical>
            <Typography.Paragraph>
              {t('profile.maximum_file')}
            </Typography.Paragraph>
            <Upload {...uploadProps}>
              <Button type="primary" loading={uploading}>
                {t('button.change_image')}
              </Button>
            </Upload>
          </Flex>
        }
      />

      <Form onFinish={onSubmit} form={form} layout="vertical">
        <Form.Item wrapperCol={{ span: 16 }} hidden name="photo" />

        <Divider />

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label={t('form.item.first_name')} name="first_name">
              <Input placeholder={t('form.item.first_name')} />
            </Form.Item>
            <Form.Item label={t('form.item.last_name')} name="last_name">
              <Input placeholder={t('form.item.last_name')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('form.item.phone')} name="phone">
              <PhoneInput />
            </Form.Item>
            {!!profile?.broker?.name && (
              <Form.Item label={t('company_name')}>
                <Input
                  placeholder={t('company_name')}
                  disabled
                  value={profile?.broker?.name}
                />
              </Form.Item>
            )}
          </Col>
        </Row>
      </Form>
    </>
  )
}
